/**
 * 축어록 핵심 포인트 (A32): 구간을 고를 때 "어디가 왜 포인트인지"를 줄마다 표시한다 (앱 화면에서만, 문서에는 넣지 않음).
 * 지도감독 기준: 위험 신호가 아니라, 치료를 실제로 진행한 흔적·반영과 구체화·내담자의 알아차림과 변화가 드러나는 구간.
 * 말투 규칙에 기반한 1차 표시다 (확정 판단이 아님).
 */
export type KeyPointKind = 'reflection' | 'specify' | 'technique' | 'insight' | 'emotion' | 'change';

export const KEY_POINT_LABEL: Record<KeyPointKind, string> = {
  reflection: '반영',
  specify: '구체화 질문',
  technique: '치료 기법',
  insight: '알아차림',
  emotion: '정서 표현',
  change: '변화',
};

const REFLECTION = /(군요|네요|구나|셨겠어요|으시겠어요|셨군요|하셨네요|것 같네요|것 같아요|것 같으세요|느끼셨|마음이셨)[.!…]*$/;
const SPECIFY = /(구체적으로|예를 들(면|어)|어떤 (순간|상황|장면|때)|그때 어떤|그 순간|조금 더 (말씀|이야기)|어떤 생각이|어떤 마음이|어떤 느낌이|무엇이 가장|어떻게 알 수)/;
/** 채택한 치료 방향이 없을 때 보는 기법의 흔적 (자동적 사고 찾기, 증거 검토, 대안적 사고, 기록·과제, 행동 실험, 척도). 채택하면 그 접근의 기법으로 본다 (A33) */
const TECHNIQUE =
  /(그 생각이|어떤 생각이 (들|드)|자동적|생각.{0,6}(근거|증거)|다르게 (볼|생각해|보면)|다른 (설명|가능성)|최악의|실제로 (일어날|그럴) 가능성|기록해|적어 (보|오)|과제|행동 실험|해 보시면|연습해|척도로|몇 점)/;
const INSIGHT =
  /(그러고 보니|알 것 같|깨달|생각해 보니|이제 보니|처음으로|제가 .{0,20}(것 같아요|거네요|거였네요|거였어요)|그렇네요|맞는 것 같아요|그래서 제가)/;
const EMOTION =
  /(불안|무섭|두려|힘들|서운|억울|답답|부끄러|외로|막막|초조|짜증|지쳤|죄책감|우울|무기력|허전|속상|위축|화가|화나|슬프|슬펐|눈물|울컥|괴롭|상처|후회|창피|수치|분노|미워|기쁘|행복|안심|편안|긴장|절망|자책)/;
const CHANGE = /(해 봤|해봤|달라졌|예전보다|이번엔|이번에는|바뀌|덜 (힘들|불안|생각)|조금 나아|처음으로 .{0,10}했)/;

export function keyPointsOf(line: { role: string; text: string }, technique: RegExp = TECHNIQUE): KeyPointKind[] {
  const t = line.text.trim();
  const out: KeyPointKind[] = [];
  if (line.role === 'counselor') {
    if (REFLECTION.test(t)) out.push('reflection');
    if (SPECIFY.test(t)) out.push('specify');
    if (technique.test(t)) out.push('technique');
  } else if (line.role === 'client') {
    if (INSIGHT.test(t)) out.push('insight');
    if (CHANGE.test(t)) out.push('change');
    if (EMOTION.test(t)) out.push('emotion');
  }
  return out;
}

/** 구간의 포인트 합계 */
export function countKeyPoints(lines: { role: string; text: string }[], technique?: RegExp): Partial<Record<KeyPointKind, number>> {
  const out: Partial<Record<KeyPointKind, number>> = {};
  for (const l of lines) for (const k of keyPointsOf(l, technique)) out[k] = (out[k] ?? 0) + 1;
  return out;
}

/**
 * 구간 점수: 여러 종류의 포인트가 고루 있을수록 높다 (가르치기 좋은 구간).
 * 같은 종류가 많아도 몇 개까지만 센다. 위험 신호는 점수에 넣지 않는다.
 */
export function keyPointScore(counts: Partial<Record<KeyPointKind, number>>): number {
  const kinds = Object.keys(counts) as KeyPointKind[];
  const weight: Record<KeyPointKind, number> = { reflection: 1.2, specify: 1.2, technique: 1.5, insight: 2, emotion: 0.6, change: 1.6 };
  let s = kinds.length * 2;
  for (const k of kinds) s += Math.min(4, counts[k] ?? 0) * weight[k];
  return s;
}

/** 포인트 요약 문장 (구간 이유의 바탕) */
export function keyPointSummary(counts: Partial<Record<KeyPointKind, number>>): string {
  return (Object.keys(KEY_POINT_LABEL) as KeyPointKind[])
    .filter((k) => counts[k])
    .map((k) => `${KEY_POINT_LABEL[k]} ${counts[k]}`)
    .join(' · ');
}
